import { gsap, ScrollToPlugin, ScrollTrigger } from 'gsap/all'
import { DrawSVGPlugin } from './library/gsap/DrawSVGPlugin'
import OnScreen from 'onscreen'
import Lenis from 'lenis'
import 'lazysizes'
import DeviceCheck from './utils/deviceCheck'
import { initStableViewportHeight, shouldRefreshScrollLayout } from './utils/viewportHeight'
import Preloader from './module/preloader'
import Header from './module/header'
import GlobalNavi from './module/globalNavi'
import SmoothScrollAnchor from './module/smoothScrollAnchor'
import ScrollPosition from './module/scrollPosition'
import FooterVideo from './module/footerVideo'
import Carousel from './module/carousel'
import LinkAnim from './module/linkAnim'
import TextAnim from './module/textAnim'
import MouseStalker from './module/mouseStalker'
import Tab from './module/tab'
import FloatingButton from './module/floatingButton'
import SideNavigation from './module/sideNavigation'

import Home from './module/_page/home'
import News from './module/_page/news'
import Recruit from './module/_page/recruit'

export default class Root {
  constructor() {
    this.html = document.querySelector('html')
    this.body = document.querySelector('body')
    this.main = document.querySelector('.l-main')

    this.SmoothScrollAnchor = new SmoothScrollAnchor()
    this.ScrollPosition = new ScrollPosition()
    this.DeviceCheck = new DeviceCheck()
    this.lenis

    this.HOME
    this.NEWS
    this.RECRUIT

    this.init()
  }

  /* ===============================================================
  * init
  // 初期設定
  * =============================================================== */
  init() {
    //scrollTo plugin使用する
    gsap.registerPlugin(DrawSVGPlugin, ScrollToPlugin, ScrollTrigger)
    ScrollTrigger.config({ ignoreMobileResize: true })

    initStableViewportHeight({
      onLayoutChange: () => {
        if (shouldRefreshScrollLayout({ force: true })) {
          ScrollTrigger.refresh()
          this.lenis?.resize()
        }
      },
    })

    //Poreloader
    this.Preloader = new Preloader()

    //Header
    this.Header = new Header()

    //GlobalNavi
    if (document.body.contains(document.querySelector('.js-gnav'))) {
      this.GlobalNavi = new GlobalNavi()
    }
  }

  /* ===============================================================
  * opening
  // 初めにサイトを開いた時の処理
  * =============================================================== */
  opening() {
    this.isPageState = 'opening'
    this.html.classList.add('is-page-loading')

    //オープニングアニメーションスタート
    const openingStart = async () => {
      await this.Preloader.opening()
      openingEnd()
    }

    //オープニングアニメーション終わった後の処理
    const openingEnd = () => {
      this.html.classList.add('is-site-opened')
      this.Preloader.openingEnd()
      this.pageTransition()
    }

    openingStart()
  }
  /* ===============================================================
  * pageLoad
  // ローディング
  * =============================================================== */
  pageLoad() {
    this.isPageState = 'loading'
    this.html.classList.add('is-page-loading')
    setTimeout(() => {
      this.pageTransition()
    }, 100)
    /* imagesLoaded(this.body, { background: true }, () => {
      this.pageTransition()
    }) */
  }

  /* ===============================================================
  * pageTransition
  // ページ遷移中の処理
  * =============================================================== */
  pageTransition() {
    this.isSiteOpen = true
    this.isPageState = 'loaded'
    this.html.classList.remove('is-page-loading')
    this.html.classList.add('is-page-loaded')
    this.DeviceCheck.getDeviceSize()
    this.Preloader.hide()
    this.setPage()
    this.setCommon()
  }

  /* ===============================================================
  * leave
  // ページ遷移前の処理
  * =============================================================== */
  leave() {
    this.isPageState = 'leave'

    // ページを離れる際にイベントなどを全てリセットする
    this.kill()

    // ローディング表示
    this.Preloader.loading()

    // ナビゲーションが開いていたら閉じる
    if (this.GlobalNavi.isOpen) {
      this.GlobalNavi.naviClose()
    }

    this.Header.hideBg()

    // クラスを削除
    const viewItems = Array.prototype.slice.call(document.querySelectorAll('.is-onscreen'))
    viewItems.forEach((el) => {
      el.classList.remove('is-onscreen')
    })
    this.html.classList.remove('is-page-loaded')
    this.html.classList.add('is-page-loading')
    this.html.classList.remove('is-scrolled')
    this.html.classList.remove('is-header-hide')
    this.html.classList.remove('is-header-invert')
  }

  /* ===============================================================
  * setPage
  // ページを表示するタイミングの処理
  * =============================================================== */
  setPage() {
    this.isPageState = 'setpage'
    this.main = document.querySelector('.l-main')

    /*
     * Bodyやナビゲーションのクラスを削除
     */
    this.body.className = ''
    const gnavItem = Array.prototype.slice.call(document.querySelectorAll('.l-gnav-menu__item'), 0)
    gnavItem.forEach((el) => {
      el.classList.remove('is-current')
    })
    /*
     * 現在のページを判別して、ナビゲーションのカレント&ページ毎に処理
     */

    const pageName = this.main.dataset.page

    if (pageName == 'home') {
      this.body.classList.add('is-home')
      this.html.classList.add('is-header-invert')
      this.HOME = new Home()
    } else {
      this.body.classList.add('is-lower')
      if (pageName == 'about') {
        this.body.classList.add('is-about')
      } else if (pageName == 'recruit') {
        this.body.classList.add('is-recruit')
        this.NEWS = new Recruit()
      } else if (pageName == 'news') {
        this.body.classList.add('is-news')
        this.NEWS = new News()
      } else if (pageName == 'contact') {
        this.body.classList.add('is-contact')
      }
    }
  }

  /* ===============================================================
   * Lenis
   * 慣性スクロール
   * =============================================================== */
  setLenis() {
    // this.lenis = new Lenis()
    const isTouch = ScrollTrigger.isTouch === 1
    this.lenis = new Lenis({
      infinite: false,
      lerp: 0.3,
      // SP: コンテンツ高さの ResizeObserver 連鎖を抑える（--app-vh で高さを固定）
      autoResize: !isTouch,
    })

    /* Gsap ScrollTriggerの統合（Lenis 公式どおり scroll ごとに即時 update） */
    // this.lenis.on('scroll', ScrollTrigger.update)

    gsap.ticker.add((time) => {
      this.lenis.raf(time * 1000)
    })

    gsap.ticker.lagSmoothing(0)
  }

  /* ===============================================================
  * setCommon
  // 共通コンポーネント
  * =============================================================== */
  setCommon() {
    //レイアウトシフト起こしそうなものは上に書く

    /*
     * スクロールの方向を取得
     *
     */
    ScrollTrigger.create({
      trigger: document.body,
      start: 0,
      end: 'max',
      onUpdate: (self) => {
        this.scrollDirection = self.direction
      },
    })

    /*
     * Carousel
     * Swiper Carousel
     */
    if (document.body.contains(document.querySelector('.swiper'))) {
      new Carousel()
    }

    /*
     * LanguageSwitcher
     * 多言語切り替えのスイッチャー
     */
    // this.languageSwitcher = new LanguageSwitcher()

    /*
     * textAnim
     * テキストアニメーション
     */
    if (document.body.contains(document.querySelector('.js-text-anim'))) {
      this.textAnim?.destroy()
      // 既存ページは reveal（旧デフォルト）を維持。新規は data-text-anim-type または options で指定
      this.textAnim = new TextAnim('.js-text-anim', { type: 'fade' })
    }

    /*
     * OnScreen
     * 示領域に入った時にクラスを付与
     */
    if (document.body.contains(document.querySelector('.js-onscreen'))) {
      const os = new OnScreen({
        tolerance: 0,
        debounce: 10,
        container: window,
      })
      os.on('enter', '.js-onscreen', (el, event) => {
        if (!el.classList.contains('is-onscreen')) {
          el.classList.add('is-onscreen')
        }
      })
    }

    if (document.querySelector('.js-onscreen-repeat')) {
      const osRepeat = new OnScreen({
        // tolerance: 100,
        tolerance: 0,
        debounce: 10,
        container: window,
      })

      osRepeat.on('enter', '.js-onscreen-repeat', (el) => {
        el.classList.add('is-onscreen')
      })

      osRepeat.on('leave', '.js-onscreen-repeat', (el) => {
        // SP: 上スクロール時の一括 remove + transition が方向転換でカクつくため無効
        if (this.DeviceCheck.getDeviceSize() === 'mobile') {
          return
        }
        // 上方向スクロール時のみ remove
        if (this.scrollDirection === -1) {
          el.classList.remove('is-onscreen')
        }
      })
    }

    /**
     * LinkAnim
     * LinkAnim
     **/
    if (document.body.contains(document.querySelector('.js-link-anim'))) {
      new LinkAnim()
    }

    /**
     * Tab
     * タブ
     **/
    if (document.body.contains(document.querySelector('.js-tab'))) {
      new Tab()
    }

    /*
     * SmoothScrollAnchor
     * スムーススクロールアンカー
     */
    new SmoothScrollAnchor()

    /*
     * FloatingButton
     * l-main 内のみ表示するフローティングボタン
     */
    if (document.querySelector('.js-floating-button')) {
      this.floatingButton?.destroy()
      this.floatingButton = new FloatingButton()
    }

    /*
     * SideNavigation
     */
    if (document.body.contains(document.querySelector('.js-side-nav'))) {
      new SideNavigation()
    }

    /*
     * MouseStalker
     * マウスストーカー
     */
    if (document.body.contains(document.querySelector('.js-mouse-stalker-area'))) {
      new MouseStalker()
    }

    /*
     * FooterVideo
     * CTA 背景動画（PC/SP で読み込みを1本に限定）
     */
    if (document.querySelector('.js-footer-video-item')) {
      this.footerVideo?.destroy()
      this.footerVideo = new FooterVideo()
    }

    /*
     * lineAnim
     * ラインアニメーション
     */
    if (document.body.contains(document.querySelector('.js-line-anim'))) {
      const targets = document.querySelectorAll('.js-line-anim')
      gsap.set(targets, { drawSVG: 0 })
      for (const target of targets) {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: target,
            start: 'top 85%',
          },
        })
        tl.to(target, {
          duration: 1.5,
          drawSVG: '100%',
          ease: 'power2.inOut',
        })
      }
    }

    /*
     * setLenis
     * 慣性スクロール
     */
    this.setLenis()
  }

  /* ===============================================================
   * kill
   * ページを離れるときにイベントなどを削除
   * (barba対応)
   * =============================================================== */
  kill() {
    gsap.killTweensOf('*')

    this.footerVideo?.destroy()
    this.footerVideo = null

    this.floatingButton?.destroy()
    this.floatingButton = null

    this.textAnim?.destroy()
    this.textAnim = null

    // ScrollTriggerを全て削除
    let triggers = ScrollTrigger.getAll()
    triggers.forEach((trigger) => {
      trigger.kill()
    })

    // lenis初期化
    this.lenis.destroy()
  }
}
